// src/components/SkillsSelection.js
import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import helpfulHintIcon from '../assets/images/helpful-hint-icon.svg';
import '../styles/SkillsSelection.css';

const SkillsSelection = ({
  categorizedSkills,
  onAddSkills,
  isTechJob,
  onSkip,

  // Keyword score props
  totalMissing = 0,
  totalPresent = 0,
  jobTitle = '',
  showPercentages = true,
  isStreamlineFlow = true, // New prop to determine flow type
}) => {
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ left: 0, top: 0 });
  const tipButtonRef = useRef(null);
  const aiBoxRef = useRef(null);
  const legendRef = useRef(null);
  const tooltipRef = useRef(null);

  const updateTooltipPosition = () => {
    if (!showTooltip || !tipButtonRef.current || !legendRef.current || !tooltipRef.current) return;

    const buttonRect = tipButtonRef.current.getBoundingClientRect();
    const legendRect = legendRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    const left = legendRect.left + legendRect.width / 2 - tooltipRect.width / 2;
    const top = buttonRect.top + buttonRect.height / 2 - tooltipRect.height / 2 - 55;

    setTooltipPosition({ left, top });
  };

  useLayoutEffect(() => {
    if (!showTooltip) return;
    const raf = requestAnimationFrame(() => {
      updateTooltipPosition();
    });
    return () => cancelAnimationFrame(raf);
  }, [showTooltip]);

  useEffect(() => {
    if (!showTooltip) return;
    window.addEventListener('resize', updateTooltipPosition);
    window.addEventListener('scroll', updateTooltipPosition, true);
    return () => {
      window.removeEventListener('resize', updateTooltipPosition);
      window.removeEventListener('scroll', updateTooltipPosition, true);
    };
  }, [showTooltip]);

  const toggleSkill = (skill) => {
    setSelectedSkills((prev) =>
      prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill]
    );
  };


  const baseMissing = Number(totalMissing) || 0;
  const basePresent = Number(totalPresent) || 0;
  const denom = baseMissing + basePresent;
  const keywordScoreRaw = denom > 0 ? (basePresent + selectedSkills.length) / denom : 0;
  
  // NEW: clamp + percent
  const keywordScore = Math.max(0, Math.min(1, keywordScoreRaw));
  const keywordPercent = Math.round(keywordScore * 100);
  
  const R = 34;
  const C = 2 * Math.PI * R;
  const dashOffset = C * (1 - keywordScore);

  // Flatten all skills with their category, sort by percentage descending, and assign global rank
  // Limit to top 30 skills if more than 30 exist
  const rankedSkills = useMemo(() => {
    const allSkills = [];
    Object.entries(categorizedSkills).forEach(([category, skills]) => {
      skills.forEach((skillObj) => {
        const skillName = typeof skillObj === 'object' && skillObj.skill ? skillObj.skill : skillObj;
        const percentage = typeof skillObj === 'object' && skillObj.percentage != null ? skillObj.percentage : 0;
        allSkills.push({ skill: skillName, percentage, category });
      });
    });
    // Sort by percentage descending
    allSkills.sort((a, b) => b.percentage - a.percentage);
    // Limit to top 30 skills
    const topSkills = allSkills.slice(0, 30);
    // Assign rank (1-based)
    return topSkills.map((item, index) => ({ ...item, rank: index + 1 }));
  }, [categorizedSkills]);


  // Define category colors and display names
  const categoryConfig = {
    "Programming Languages": {
      color: "programming",
      displayName: "Programming Languages",
      icon: "💻"
    },
    "Applications": {
      color: "applications", 
      displayName: "Applications",
      icon: "🔧"
    },
    "Technical Skills": {
      color: "technical",
      displayName: "Technical Skills", 
      icon: "⚙️"
    },
    "Non-Technical Skills": {
      color: "non-technical",
      displayName: "Non-Technical Skills",
      icon: "🎯"
    },
    "Domain Experience": {
      color: "domain",
      displayName: "Domain Experience",
      icon: "📚"
    }
  };

  // In SkillsSelection.js
  return (
    <div className="skills-selection-ai-container">
      <div className="ai-loading-container">
        <div className="ai-loading-box" ref={aiBoxRef}>
          <div className="ai-content">
            <div className="skills-selection-content">
              {/* <h2>Click the missing skills below to add them to your resume</h2> */}
              <h2>Click missing skills to add them to your resume</h2>
              <div className="skills-top-row">
                <div className="score-card">
                  <div className="score-title">Your Keyword Score</div>
                  <div className="keyword-score-circle" title={`Keyword score: ${keywordPercent}`}>
                    <svg className="keyword-score-ring" viewBox="0 0 80 80" aria-hidden="true">
                      <circle className="keyword-score-ring-bg" cx="40" cy="40" r={R} />
                      <circle className="keyword-score-ring-inner" cx="40" cy="40" r={R - 4} />
                      <circle
                        className="keyword-score-ring-fg"
                        cx="40"
                        cy="40"
                        r={R}
                        style={{
                          strokeDasharray: `${C} ${C}`,
                          strokeDashoffset: dashOffset,
                        }}
                      />
                    </svg>
                    <div className="keyword-score-text">{keywordPercent}</div>
                  </div>
                  <div className="score-copy">
                  {isStreamlineFlow 
                    ? "The percent of in-demand keywords for your job title that were detected in your resume. Click on skills you have to improve your score."
                    : "The percent of in-demand keywords for this position that were detected in your resume. Click on skills you have to improve your score."
                  }
                  </div>
                  <button
                    className="score-tip"
                    type="button"
                    ref={tipButtonRef}
                    onClick={() => setShowTooltip(true)}
                  >
                    Helpful Tip
                  </button>
                </div>

                <div className="legend-card" ref={legendRef}>
                  <div className="skills-legend">
                    {Object.entries(categorizedSkills).map(([category, skills]) => {
                      if (skills.length === 0) return null;
                      const config = categoryConfig[category];
                      return (
                        <div key={category} className="legend-item">
                          <div className={`legend-color skill-item-${config.color}`}></div>
                          <span>{config.displayName}</span>
                        </div>
                      );
                    })}
                    {showPercentages && (
                      <div className="legend-item legend-percent-info">
                        <span className="legend-percent-badge">%</span>
                        <span>Recent <b>{jobTitle ? `${jobTitle} ` : ''}</b>jobs mentioning skill</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="skills-grid">
                {rankedSkills.map((item) => {
                  const config = categoryConfig[item.category];
                  return (
                    <div
                      key={`${item.category}-${item.skill}`}
                      className={`skill-item skill-item-${config.color} ${selectedSkills.includes(item.skill) ? 'selected' : ''}`}
                      onClick={() => toggleSkill(item.skill)}
                    >
                      <span className="skill-add-indicator" aria-hidden="true">
                        {selectedSkills.includes(item.skill) ? '✓' : '+'}
                      </span>
                      <span className="skill-name">{item.skill}</span>
                      {showPercentages && <span className="skill-percent">{Math.round(item.percentage)}%</span>}
                    </div>
                  );
                })}
              </div>

              <div className="skills-footer">
                <button className="clear-button" onClick={() => setSelectedSkills([])}>
                  Clear
                </button>
                <button
                  className="continue-button"
                  onClick={() => onAddSkills(selectedSkills)}
                  //disabled={selectedSkills.length === 0}
                >
                  Continue
                </button>
              </div>

            </div>

          </div>
        </div>
      </div>

      {/* Simple Tooltip */}
      {showTooltip && (
        <>
          <div className="tooltip-overlay" onClick={() => setShowTooltip(false)}></div>
          <div
            className="simple-tooltip" 
            ref={tooltipRef}
            style={{ 
              left: `${tooltipPosition.left}px`, 
              top: `${tooltipPosition.top}px` 
            }}
          >
            <button className="tooltip-close" onClick={() => setShowTooltip(false)}>×</button>
            <div className="tooltip-icon">
              <img
                src={helpfulHintIcon}
                alt=""
                className="tooltip-icon-image"
              />
            </div>
            <h3 className="tooltip-title">Take a look at what you're missing</h3>
            <p>Make note of the in-demand skills to upskill and increase your chances of employment.</p>
            <div className="tooltip-arrow"></div>
          </div>
        </>
      )}
    </div>
  );
};
export default SkillsSelection;
